import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleModal } from '@/components/SimpleModal';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/Toast';
import { 
  Building2, DollarSign, Users, CheckCircle, XCircle, Lock, Unlock, 
  TrendingUp, CreditCard, AlertTriangle, Eye, Edit, Trash2, Plus,
  Calendar, RefreshCw, BarChart3, Crown, Settings, EyeOff, X, ShoppingCart, MessageCircle, ShoppingBag, Store,
  UserPlus, Key, Package, Mail, QrCode, Wallet, Shield as ShieldIcon, Database, PlayCircle, CheckCircle2,
  FileText, Star
} from 'lucide-react';
import BlogAdminPanel from '@/components/BlogAdminPanel';
import TestimonialsAdminPanel from '@/components/TestimonialsAdminPanel';

export const SuperAdminPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  
  // Modals
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showNewPlanModal, setShowNewPlanModal] = useState(false);
  
  // Configurações de Pagamento SaaS
  const [saasPaymentConfig, setSaasPaymentConfig] = useState({
    mercadopago_habilitado: false,
    mercadopago_access_token: '',
    pix_timeout_minutos: 30
  });
  const [showMpToken, setShowMpToken] = useState(false);
  
  // Configuração de Gateway de Pagamento
  const [paymentGatewayConfig, setPaymentGatewayConfig] = useState({
    provider: 'mercadopago',
    access_token: '',
    public_key: '',
    sandbox_mode: true
  });
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [savingPaymentConfig, setSavingPaymentConfig] = useState(false);
  
  // Migração de Dados
  const [migracaoStatus, setMigracaoStatus] = useState(null);
  const [migracaoLoading, setMigracaoLoading] = useState(false);
  const [migracaoResultado, setMigracaoResultado] = useState(null);
  
  // Criar/Reativar Usuário
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    plan_id: 'professional',
    create_sample_products: true
  });
  const [createUserLoading, setCreateUserLoading] = useState(false);
  
  // Resetar Senha
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    email: '',
    new_password: ''
  });
  
  // Edit states
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [newPlan, setNewPlan] = useState({
    id: '',
    name: '',
    price: 0,
    max_products: 100,
    max_users: 5,
    max_sales_per_month: 1000,
    features: [],
    modules: { pdv: true, whatsapp: false, online: false }
  });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    if (user?.role === 'super_admin') {
      loadAllData();
      loadSaasPaymentSettings();
      loadPaymentGatewayConfig();
    }
  }, [user]);
  
  const loadPaymentGatewayConfig = async () => {
    try {
      const response = await api.get('/admin/payment-config');
      if (response.data) {
        setPaymentGatewayConfig(prev => ({
          ...prev,
          provider: response.data.provider || 'mercadopago',
          public_key: response.data.public_key || '',
          sandbox_mode: response.data.sandbox_mode ?? true,
          access_token: response.data.configured ? '********' : ''
        }));
      }
    } catch (error) {
      console.log('Erro ao carregar config gateway de pagamento');
    }
  };

  const handleSavePaymentGatewayConfig = async () => {
    if (!paymentGatewayConfig.access_token || paymentGatewayConfig.access_token === '********') {
      toast.warning('Digite o Access Token');
      return;
    }
    if (!paymentGatewayConfig.public_key) {
      toast.warning('Digite a Public Key');
      return;
    }
    
    setSavingPaymentConfig(true);
    try {
      await api.post('/admin/payment-config', paymentGatewayConfig);
      toast.success('Configuração do gateway de pagamento salva!');
      loadPaymentGatewayConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
    } finally {
      setSavingPaymentConfig(false);
    }
  };
  
  const loadSaasPaymentSettings = async () => {
    try {
      const response = await api.get('/superadmin/settings/pagamento');
      if (response.data) {
        setSaasPaymentConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('Erro ao carregar config pagamento SaaS');
    }
  };

  const handleSaveSaasPaymentSettings = async () => {
    setLoading(true);
    try {
      await api.post('/superadmin/settings/pagamento', saasPaymentConfig);
      toast.success('Configurações de pagamento SaaS salvas!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [tenantsRes, plansRes, subsRes, dashRes] = await Promise.all([
        api.get('/admin/tenants'),
        api.get('/admin/plans'),
        api.get('/admin/subscriptions'),
        api.get('/admin/dashboard')
      ]);
      setTenants(tenantsRes.data);
      setPlans(plansRes.data.plans || []);
      setSubscriptions(subsRes.data.subscriptions || []);
      setDashboard(dashRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do painel');
    } finally {
      setLoading(false);
    }
  };

  // ===== FUNÇÕES DE MIGRAÇÃO =====
  const verificarSincronizacao = async () => {
    setMigracaoLoading(true);
    try {
      const response = await api.get('/admin/verificar-sincronizacao');
      setMigracaoStatus(response.data);
      toast.success('Verificação concluída!');
    } catch (error) {
      console.error('Erro ao verificar sincronização:', error);
      toast.error('Erro ao verificar sincronização: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMigracaoLoading(false);
    }
  };

  const executarMigracao = async () => {
    if (!window.confirm('Tem certeza que deseja executar a migração? Esta ação irá sincronizar os dados de todos os tenants para o sistema fiscal.')) {
      return;
    }
    
    setMigracaoLoading(true);
    try {
      const response = await api.post('/admin/migrar-tenants-para-settings');
      setMigracaoResultado(response.data);
      toast.success(response.data.message || 'Migração concluída com sucesso!');
      // Atualizar status após migração
      await verificarSincronizacao();
    } catch (error) {
      console.error('Erro ao executar migração:', error);
      toast.error('Erro ao executar migração: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMigracaoLoading(false);
    }
  };

  const handleViewTenantDetails = async (tenantId) => {
    try {
      const response = await api.get(`/admin/tenants/${tenantId}`);
      setTenantDetails(response.data);
      setShowTenantModal(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da empresa');
    }
  };

  const handleToggleTenantStatus = async (tenantId, currentStatus) => {
    const shouldSuspend = currentStatus === 'active' || currentStatus === 'trial';
    const action = shouldSuspend ? 'suspender' : 'reativar';

    if (!window.confirm(`Tem certeza que deseja ${action} esta empresa?`)) return;

    try {
      const endpoint = shouldSuspend
        ? `/admin/tenants/${tenantId}/suspend`
        : `/admin/tenants/${tenantId}/reactivate`;
      const body = shouldSuspend ? { reason: 'Suspenso pelo Super Admin' } : {};
      await api.post(endpoint, body);
      loadAllData();
      toast.success(`Empresa ${shouldSuspend ? 'suspensa' : 'reativada'} com sucesso!`);
    } catch (error) {
      toast.error(`Erro ao ${action} empresa`);
    }
  };

  const handleExtendTenant = async (tenantId, defaultDays = 30) => {
    const input = window.prompt(`Estender assinatura por quantos dias?`, String(defaultDays));
    if (input === null) return;
    const days = parseInt(input, 10);
    if (!days || days <= 0) { toast.error('Informe uma quantidade válida de dias'); return; }
    try {
      const { data } = await api.post(`/admin/tenants/${tenantId}/extend`, { days });
      loadAllData();
      const newEnd = data?.subscription_end ? new Date(data.subscription_end).toLocaleDateString('pt-BR') : '';
      toast.success(`Assinatura estendida! Novo vencimento: ${newEnd}`);
    } catch (error) {
      toast.error('Erro ao estender assinatura');
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Tem certeza que deseja CANCELAR esta empresa? Esta ação não pode ser desfeita.')) return;

    try {
      await api.delete(`/admin/tenants/${tenantId}`);
      loadAllData();
      toast.success('Empresa cancelada com sucesso');
    } catch (error) {
      toast.error('Erro ao cancelar empresa');
    }
  };

  const handleUpdatePlan = async () => {
    if (!editingPlan) return;
    
    // Fechar modal PRIMEIRO
    setShowPlanModal(false);
    const planData = { ...editingPlan };
    setEditingPlan(null);
    
    try {
      await api.put(`/admin/plans/${planData.id}`, planData);
      loadAllData();
      toast.success('Plano atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar plano');
    }
  };

  const handleCreatePlan = async () => {
    if (!newPlan.name || !newPlan.price) {
      toast.warning('Preencha nome e preço do plano');
      return;
    }
    
    // Fechar modal PRIMEIRO
    setShowNewPlanModal(false);
    const planData = { ...newPlan };
    setNewPlan({ id: '', name: '', price: 0, max_products: 100, max_users: 5, max_sales_per_month: 1000, features: [] });
    
    try {
      await api.post('/admin/plans', planData);
      loadAllData();
      toast.success('Plano criado com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar plano');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Tem certeza que deseja desativar este plano?')) return;
    
    try {
      await api.delete(`/admin/plans/${planId}`);
      loadAllData();
      toast.success('Plano desativado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar plano');
    }
  };

  const handleUpdateSubscription = async () => {
    if (!editingSubscription) return;
    
    // Fechar modal PRIMEIRO
    setShowSubscriptionModal(false);
    const subData = { ...editingSubscription };
    setEditingSubscription(null);
    
    try {
      const updateData = {};
      if (subData.newPlanId) {
        updateData.plan_id = subData.newPlanId;
      }
      if (subData.newStatus) {
        updateData.status = subData.newStatus;
      }
      if (subData.extendDays) {
        updateData.extend_days = parseInt(subData.extendDays);
      }
      
      await api.put(`/admin/subscriptions/${subData.tenant_id}`, updateData);
      loadAllData();
      toast.success('Assinatura atualizada com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar assinatura');
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setNewPlan({ ...newPlan, features: [...newPlan.features, newFeature.trim()] });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setNewPlan({ ...newPlan, features: newPlan.features.filter((_, i) => i !== index) });
  };

  // Criar/Reativar Tenant e Usuário
  const handleCreateTenantUser = async () => {
    if (!createUserForm.email || !createUserForm.password || !createUserForm.name || !createUserForm.company_name) {
      toast.warning('Preencha todos os campos obrigatórios');
      return;
    }
    
    setCreateUserLoading(true);
    try {
      const response = await api.post('/admin/create-tenant-user', createUserForm);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowCreateUserModal(false);
        setCreateUserForm({
          email: '',
          password: '',
          name: '',
          company_name: '',
          plan_id: 'professional',
          create_sample_products: true
        });
        loadAllData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    } finally {
      setCreateUserLoading(false);
    }
  };

  // Resetar Senha de Usuário
  const handleResetPassword = async () => {
    if (!resetPasswordForm.email || !resetPasswordForm.new_password) {
      toast.warning('Preencha email e nova senha');
      return;
    }
    
    try {
      const response = await api.post(`/admin/reset-user-password?email=${encodeURIComponent(resetPasswordForm.email)}&new_password=${encodeURIComponent(resetPasswordForm.new_password)}`);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowResetPasswordModal(false);
        setResetPasswordForm({ email: '', new_password: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-rose-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-rose-600 mb-2">Acesso Negado</h1>
            <p className="text-slate-600">Apenas o super administrador pode acessar esta página.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="super-admin-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Crown className="w-8 h-8 text-amber-500" />
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Painel Super Admin
            </h1>
          </div>
          <p className="text-slate-600">Gestão completa do SaaS nfcesysPdv</p>
        </div>
        <Button onClick={loadAllData} disabled={loading} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Dashboard Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Total Empresas</p>
                  <p className="text-3xl font-bold">{dashboard.tenants.total}</p>
                </div>
                <Building2 className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Ativas</p>
                  <p className="text-3xl font-bold text-emerald-600">{dashboard.tenants.active}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Em Trial</p>
                  <p className="text-3xl font-bold text-blue-600">{dashboard.tenants.trial}</p>
                </div>
                <Calendar className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Suspensas</p>
                  <p className="text-3xl font-bold text-rose-600">{dashboard.tenants.suspended}</p>
                </div>
                <Lock className="w-8 h-8 text-rose-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-700">Receita Mensal</p>
                  <p className="text-3xl font-bold text-amber-700">{formatCurrency(dashboard.revenue.monthly_estimated)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-amber-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Expiring Soon Alert */}
      {dashboard?.expiring_soon?.length > 0 && (
        <Card className="mb-6 border-amber-300 bg-amber-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Assinaturas expirando em 7 dias</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {dashboard.expiring_soon.map(t => (
                <span key={t.id} className="px-3 py-1 bg-amber-200 text-amber-800 rounded-full text-sm">
                  {t.company_name}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="tenants" className="w-full">
        <TabsList className="grid w-full grid-cols-9 mb-6">
          <TabsTrigger value="tenants">
            <Building2 className="w-4 h-4 mr-2" />
            Empresas
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" />
            Usuários
          </TabsTrigger>
          <TabsTrigger value="subscriptions">
            <CreditCard className="w-4 h-4 mr-2" />
            Assinaturas
          </TabsTrigger>
          <TabsTrigger value="plans">
            <DollarSign className="w-4 h-4 mr-2" />
            Planos
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="settings">
            <Settings className="w-4 h-4 mr-2" />
            Configurações
          </TabsTrigger>
          <TabsTrigger value="migracao">
            <Database className="w-4 h-4 mr-2" />
            Migração
          </TabsTrigger>
          <TabsTrigger value="blog" data-testid="superadmin-tab-blog">
            <FileText className="w-4 h-4 mr-2" />
            Blog
          </TabsTrigger>
          <TabsTrigger value="depoimentos" data-testid="superadmin-tab-depoimentos">
            <Star className="w-4 h-4 mr-2" />
            Depoimentos
          </TabsTrigger>
        </TabsList>

        {/* Tenants Tab */}
        <TabsContent value="tenants">
          <Card>
            <CardHeader>
              <CardTitle>Gerenciar Empresas</CardTitle>
              <CardDescription>Lista de todas as empresas cadastradas no sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expira em</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map(tenant => {
                    const endDate = tenant.subscription_end ? new Date(tenant.subscription_end) : null;
                    const daysRemaining = endDate ? Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24)) : 0;
                    const plan = plans.find(p => p.id === tenant.plan_id);
                    
                    return (
                      <TableRow key={tenant.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="font-semibold">{tenant.company_name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{tenant.owner_email}</TableCell>
                        <TableCell>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                            {plan?.name || tenant.plan_id}
                          </span>
                        </TableCell>
                        <TableCell>
                          {tenant.status === 'active' && (
                            <span className="flex items-center gap-1 text-emerald-600">
                              <CheckCircle className="w-4 h-4" /> Ativo
                            </span>
                          )}
                          {tenant.status === 'trial' && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <Calendar className="w-4 h-4" /> Trial
                            </span>
                          )}
                          {tenant.status === 'suspended' && (
                            <span className="flex items-center gap-1 text-rose-600">
                              <XCircle className="w-4 h-4" /> Suspenso
                            </span>
                          )}
                          {tenant.status === 'cancelled' && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <XCircle className="w-4 h-4" /> Cancelado
                            </span>
                          )}
                        </TableCell>
                        <TableCell className={`text-sm ${daysRemaining <= 7 ? 'text-rose-600 font-semibold' : ''}`}>
                          {daysRemaining > 0 ? `${daysRemaining} dias` : 'Expirado'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleViewTenantDetails(tenant.id)} data-testid={`view-tenant-${tenant.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleExtendTenant(tenant.id, 30)}
                              className="text-blue-600"
                              title="Estender assinatura (30 dias)"
                              data-testid={`extend-tenant-${tenant.id}`}
                            >
                              <Calendar className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleTenantStatus(tenant.id, tenant.status)}
                              className={tenant.status === 'suspended' ? 'text-emerald-600' : 'text-amber-600'}
                              title={tenant.status === 'suspended' ? 'Reativar empresa' : 'Suspender empresa'}
                              data-testid={`toggle-tenant-${tenant.id}`}
                            >
                              {tenant.status === 'suspended' ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteTenant(tenant.id)} className="text-rose-600" title="Cancelar empresa" data-testid={`delete-tenant-${tenant.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab - Criar/Reativar Usuários */}
        <TabsContent value="users">
          <div className="grid gap-6">
            {/* Criar/Reativar Tenant e Usuário */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-emerald-600" />
                  Criar ou Reativar Cliente
                </CardTitle>
                <CardDescription>
                  Crie um novo tenant com usuário admin ou reative um cliente excluído
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Formulário de criação */}
                  <div className="space-y-4 border rounded-lg p-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Plus className="w-5 h-5" />
                      Criar Novo Cliente
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          placeholder="cliente@email.com"
                          value={createUserForm.email}
                          onChange={(e) => setCreateUserForm({...createUserForm, email: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Senha *</Label>
                        <Input
                          type="text"
                          placeholder="123456"
                          value={createUserForm.password}
                          onChange={(e) => setCreateUserForm({...createUserForm, password: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nome do Usuário *</Label>
                        <Input
                          placeholder="Nome completo"
                          value={createUserForm.name}
                          onChange={(e) => setCreateUserForm({...createUserForm, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Nome da Empresa *</Label>
                        <Input
                          placeholder="Nome da empresa"
                          value={createUserForm.company_name}
                          onChange={(e) => setCreateUserForm({...createUserForm, company_name: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Plano</Label>
                        <select
                          value={createUserForm.plan_id}
                          onChange={(e) => setCreateUserForm({...createUserForm, plan_id: e.target.value})}
                          className="w-full h-10 px-3 border border-slate-300 rounded-md"
                        >
                          {plans.map(p => (
                            <option key={p.id} value={p.id}>{p.name} - {formatCurrency(p.price)}/mês</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={createUserForm.create_sample_products}
                            onChange={(e) => setCreateUserForm({...createUserForm, create_sample_products: e.target.checked})}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                          <span className="text-sm">Criar produtos de exemplo</span>
                        </label>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={handleCreateTenantUser} 
                      disabled={createUserLoading}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      {createUserLoading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Processando...
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4 mr-2" />
                          Criar/Reativar Cliente
                        </>
                      )}
                    </Button>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                      <strong>Nota:</strong> Se o email já existir, o sistema irá reativar o usuário e atualizar a senha.
                    </div>
                  </div>
                  
                  {/* Resetar Senha */}
                  <div className="space-y-4 border rounded-lg p-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Key className="w-5 h-5" />
                      Resetar Senha de Usuário
                    </h3>
                    
                    <div>
                      <Label>Email do Usuário *</Label>
                      <Input
                        type="email"
                        placeholder="usuario@email.com"
                        value={resetPasswordForm.email}
                        onChange={(e) => setResetPasswordForm({...resetPasswordForm, email: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <Label>Nova Senha *</Label>
                      <Input
                        type="text"
                        placeholder="nova_senha123"
                        value={resetPasswordForm.new_password}
                        onChange={(e) => setResetPasswordForm({...resetPasswordForm, new_password: e.target.value})}
                      />
                    </div>
                    
                    <Button 
                      onClick={handleResetPassword}
                      variant="outline"
                      className="w-full"
                    >
                      <Key className="w-4 h-4 mr-2" />
                      Resetar Senha
                    </Button>
                    
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                      <strong>Atenção:</strong> Ao resetar a senha, o usuário será reativado automaticamente se estiver desativado.
                    </div>
                    
                    {/* Diagnosticar e Corrigir */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="font-medium mb-2">Diagnosticar / Corrigir Problemas</h4>
                      <div className="flex gap-2 mb-2">
                        <Button 
                          onClick={async () => {
                            if (!resetPasswordForm.email) {
                              toast.warning('Digite o email do usuário');
                              return;
                            }
                            try {
                              const response = await api.get(`/admin/diagnose-user/${encodeURIComponent(resetPasswordForm.email)}`);
                              const data = response.data;
                              let msg = `Diagnóstico de ${resetPasswordForm.email}:\n\n`;
                              msg += `Tenant: ${data.tenant ? data.tenant.company_name : 'NÃO ENCONTRADO'}\n`;
                              msg += `Assinatura: ${data.subscription ? data.subscription.status : 'NÃO ENCONTRADA'}\n`;
                              msg += `Produtos Total: ${data.products_count}\n`;
                              msg += `Produtos Ativos: ${data.products_active}\n\n`;
                              if (data.issues && data.issues.length > 0) {
                                msg += `PROBLEMAS:\n- ${data.issues.join('\n- ')}`;
                              } else {
                                msg += 'Nenhum problema encontrado.';
                              }
                              alert(msg);
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Erro ao diagnosticar');
                            }
                          }}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Diagnosticar
                        </Button>
                        <Button 
                          onClick={async () => {
                            if (!resetPasswordForm.email) {
                              toast.warning('Digite o email do usuário');
                              return;
                            }
                            if (!window.confirm(`Corrigir problemas do usuário ${resetPasswordForm.email}?`)) return;
                            try {
                              const response = await api.post(`/admin/fix-user/${encodeURIComponent(resetPasswordForm.email)}?create_products=true`);
                              if (response.data.success) {
                                toast.success(`Correções aplicadas: ${response.data.fixes_applied.join(', ') || 'Nenhuma necessária'}`);
                                loadAllData();
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Erro ao corrigir');
                            }
                          }}
                          variant="outline"
                          size="sm"
                          className="flex-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Corrigir
                        </Button>
                      </div>
                      <Button 
                        onClick={async () => {
                          if (!resetPasswordForm.email) {
                            toast.warning('Digite o email do usuário');
                            return;
                          }
                          if (!window.confirm(`Ativar TODOS os produtos do usuário ${resetPasswordForm.email}?`)) return;
                          try {
                            const response = await api.post(`/admin/activate-products/${encodeURIComponent(resetPasswordForm.email)}`);
                            if (response.data.success) {
                              toast.success(`${response.data.activated} produtos ativados! Total ativos: ${response.data.active_after}`);
                              loadAllData();
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Erro ao ativar produtos');
                          }
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
                      >
                        <Package className="w-4 h-4 mr-1" />
                        Ativar Todos os Produtos
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Ações Rápidas */}
            <Card>
              <CardHeader>
                <CardTitle>Ações Rápidas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button 
                    variant="outline" 
                    className="h-20 flex-col"
                    onClick={() => {
                      setCreateUserForm({
                        email: 'alexadrianotecido@gmail.com',
                        password: '123456',
                        name: 'Alexandria Tecidos',
                        company_name: 'Alexandria Tecidos',
                        plan_id: 'professional',
                        create_sample_products: true
                      });
                    }}
                  >
                    <Package className="w-6 h-6 mb-2 text-blue-600" />
                    <span className="text-xs">Preencher Alexandria</span>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="h-20 flex-col"
                    onClick={() => {
                      setResetPasswordForm({
                        email: 'alexadrianotecido@gmail.com',
                        new_password: '123456'
                      });
                    }}
                  >
                    <Key className="w-6 h-6 mb-2 text-amber-600" />
                    <span className="text-xs">Reset Alexandria</span>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="h-20 flex-col"
                    onClick={loadAllData}
                  >
                    <RefreshCw className="w-6 h-6 mb-2 text-slate-600" />
                    <span className="text-xs">Atualizar Dados</span>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="h-20 flex-col text-red-600 border-red-200 hover:bg-red-50"
                    onClick={async () => {
                      const email = prompt('Digite o email do usuário para DELETAR COMPLETAMENTE:');
                      if (!email) return;
                      if (!window.confirm(`⚠️ ATENÇÃO!\n\nIsso vai DELETAR PERMANENTEMENTE:\n- Usuário: ${email}\n- Tenant\n- Assinatura\n- Todos os produtos\n- Vendas\n- Clientes\n\nTem certeza?`)) return;
                      if (!window.confirm(`ÚLTIMA CONFIRMAÇÃO!\n\nDeletar ${email} e TODOS os dados?`)) return;
                      
                      try {
                        const response = await api.delete(`/admin/delete-user-complete/${encodeURIComponent(email)}`);
                        if (response.data.success) {
                          toast.success(`Usuário ${email} deletado completamente!`);
                          loadAllData();
                        }
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Erro ao deletar');
                      }
                    }}
                  >
                    <Trash2 className="w-6 h-6 mb-2" />
                    <span className="text-xs">Deletar Usuário</span>
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="h-20 flex-col"
                    onClick={() => window.open('/tenants', '_blank')}
                  >
                    <Building2 className="w-6 h-6 mb-2 text-emerald-600" />
                    <span className="text-xs">Ver Empresas</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          <Card>
            <CardHeader>
              <CardTitle>Gerenciar Assinaturas</CardTitle>
              <CardDescription>Controle de assinaturas e pagamentos</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Início</TableHead>
                    <TableHead>Fim</TableHead>
                    <TableHead>Dias Rest.</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map(sub => (
                    <TableRow key={sub.tenant_id}>
                      <TableCell className="font-medium">{sub.company_name}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                          {sub.plan_name}
                        </span>
                      </TableCell>
                      <TableCell className="font-semibold">{formatCurrency(sub.plan_price)}/mês</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-sm ${
                          sub.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                          sub.status === 'trial' ? 'bg-blue-100 text-blue-700' :
                          'bg-rose-100 text-rose-700'
                        }`}>
                          {sub.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {sub.subscription_start ? new Date(sub.subscription_start).toLocaleDateString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {sub.subscription_end ? new Date(sub.subscription_end).toLocaleDateString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell className={`text-sm font-medium ${sub.days_remaining <= 7 ? 'text-rose-600' : ''}`}>
                        {sub.days_remaining} dias
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingSubscription({ ...sub, newPlanId: '', newStatus: '', extendDays: '' });
                            setShowSubscriptionModal(true);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" /> Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Gerenciar Planos</CardTitle>
                <CardDescription>Configure os planos e preços do SaaS</CardDescription>
              </div>
              <Button onClick={() => setShowNewPlanModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" /> Novo Plano
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.filter(p => p.active !== false).map(plan => (
                  <Card key={plan.id} className="relative">
                    {dashboard?.tenants?.by_plan?.[plan.id] > 0 && (
                      <div className="absolute top-2 right-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                          {dashboard.tenants.by_plan[plan.id]} empresas
                        </span>
                      </div>
                    )}
                    <CardContent className="pt-6">
                      <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                      <div className="text-3xl font-bold text-emerald-600 mb-4">
                        {formatCurrency(plan.price)}
                        <span className="text-base text-slate-600 font-normal">/mês</span>
                      </div>
                      
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Produtos:</span>
                          <span className="font-medium">{plan.max_products === -1 ? 'Ilimitado' : plan.max_products}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Usuários:</span>
                          <span className="font-medium">{plan.max_users === -1 ? 'Ilimitado' : plan.max_users}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Vendas/mês:</span>
                          <span className="font-medium">{plan.max_sales_per_month === -1 ? 'Ilimitado' : plan.max_sales_per_month}</span>
                        </div>
                      </div>
                      
                      {plan.features?.length > 0 && (
                        <ul className="space-y-1 mb-4">
                          {plan.features.slice(0, 4).map((feature, i) => (
                            <li key={i} className="text-sm text-slate-600 flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-emerald-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => { setEditingPlan({...plan}); setShowPlanModal(true); }}
                        >
                          <Edit className="w-4 h-4 mr-1" /> Editar
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-rose-600"
                          onClick={() => handleDeletePlan(plan.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Top Tenants */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Empresas por Vendas</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.top_tenants?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.top_tenants.map((t, i) => (
                      <div key={t._id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-amber-700' : 'bg-slate-300'
                          }`}>
                            {i + 1}
                          </span>
                          <span className="font-medium">{t.company_name}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">{formatCurrency(t.total_sales)}</p>
                          <p className="text-xs text-slate-500">{t.count} vendas</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">Nenhuma venda registrada</p>
                )}
              </CardContent>
            </Card>

            {/* Revenue Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Resumo Financeiro</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard && (
                  <div className="space-y-4">
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <p className="text-sm text-emerald-700">Receita Mensal Estimada</p>
                      <p className="text-3xl font-bold text-emerald-700">{formatCurrency(dashboard.revenue.monthly_estimated)}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-700">Receita Anual Estimada</p>
                      <p className="text-3xl font-bold text-blue-700">{formatCurrency(dashboard.revenue.annual_estimated)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-sm text-slate-600">Vendas Plataforma (mês)</p>
                        <p className="text-xl font-bold">{dashboard.platform_sales.month_count}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-sm text-slate-600">Volume (mês)</p>
                        <p className="text-xl font-bold">{formatCurrency(dashboard.platform_sales.month_value)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="grid gap-6">
            {/* Gateway de Pagamento */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  Gateway de Pagamento - Cobrança de Licenças
                </CardTitle>
                <CardDescription>
                  Configure o Mercado Pago ou PagSeguro para receber pagamentos de assinaturas dos clientes via PIX e Cartão
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Seleção do Provider */}
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    onClick={() => setPaymentGatewayConfig({...paymentGatewayConfig, provider: 'mercadopago'})}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      paymentGatewayConfig.provider === 'mercadopago' 
                        ? 'border-[#009EE3] bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#009EE3] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-lg">MP</span>
                      </div>
                      <div>
                        <h3 className="font-semibold">Mercado Pago</h3>
                        <p className="text-xs text-slate-500">PIX, Cartão, Boleto</p>
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    onClick={() => setPaymentGatewayConfig({...paymentGatewayConfig, provider: 'pagseguro'})}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      paymentGatewayConfig.provider === 'pagseguro' 
                        ? 'border-[#00A859] bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#00A859] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-lg">PS</span>
                      </div>
                      <div>
                        <h3 className="font-semibold">PagSeguro</h3>
                        <p className="text-xs text-slate-500">PIX, Cartão, Boleto</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Configurações do Gateway Selecionado */}
                <div className="border rounded-lg p-4 space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <ShieldIcon className="w-4 h-4 text-amber-500" />
                    Credenciais {paymentGatewayConfig.provider === 'mercadopago' ? 'Mercado Pago' : 'PagSeguro'}
                  </h3>
                  
                  <div className="grid gap-4">
                    <div>
                      <Label>Access Token *</Label>
                      <div className="relative mt-1">
                        <Input
                          type={showAccessToken ? "text" : "password"}
                          value={paymentGatewayConfig.access_token}
                          onChange={(e) => setPaymentGatewayConfig({...paymentGatewayConfig, access_token: e.target.value})}
                          placeholder={paymentGatewayConfig.provider === 'mercadopago' ? 'APP_USR-0000000000000000-...' : 'Seu token PagSeguro'}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowAccessToken(!showAccessToken)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showAccessToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Public Key *</Label>
                      <Input
                        value={paymentGatewayConfig.public_key}
                        onChange={(e) => setPaymentGatewayConfig({...paymentGatewayConfig, public_key: e.target.value})}
                        placeholder={paymentGatewayConfig.provider === 'mercadopago' ? 'APP_USR-xxxxxxxx-xxxx-xxxx...' : 'Sua public key PagSeguro'}
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Usada no frontend para tokenização segura de cartões
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Switch
                        checked={paymentGatewayConfig.sandbox_mode}
                        onCheckedChange={(checked) => setPaymentGatewayConfig({...paymentGatewayConfig, sandbox_mode: checked})}
                      />
                      <div>
                        <Label>Modo Sandbox (Testes)</Label>
                        <p className="text-xs text-slate-500">Ative para testar sem cobranças reais</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t flex justify-between items-center">
                    <div className="text-sm text-slate-500">
                      {paymentGatewayConfig.provider === 'mercadopago' ? (
                        <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <Key className="w-3 h-3" />
                          Obter credenciais no Mercado Pago
                        </a>
                      ) : (
                        <a href="https://pagseguro.uol.com.br/preferencias/integracoes.jhtml" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                          <Key className="w-3 h-3" />
                          Obter credenciais no PagSeguro
                        </a>
                      )}
                    </div>
                    
                    <Button 
                      onClick={handleSavePaymentGatewayConfig}
                      disabled={savingPaymentConfig}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {savingPaymentConfig ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Salvar Configuração
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Métodos de Pagamento */}
                <div className="bg-slate-50 border rounded-lg p-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    Métodos de Pagamento Disponíveis
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="flex items-center gap-2 bg-white p-3 rounded-lg border">
                      <QrCode className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-sm">PIX</p>
                        <p className="text-xs text-slate-500">Instantâneo</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-white p-3 rounded-lg border">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-sm">Cartão</p>
                        <p className="text-xs text-slate-500">Crédito/Débito</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 bg-white p-3 rounded-lg border opacity-50">
                      <DollarSign className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="font-medium text-sm">Boleto</p>
                        <p className="text-xs text-slate-500">Em breve</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Como funciona:</strong> Quando um cliente for renovar ou fazer upgrade do plano, 
                    o sistema gerará um QR Code PIX ou processará o cartão. Após o pagamento ser confirmado, 
                    a licença será ativada automaticamente e o cliente receberá confirmação por email e WhatsApp.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Migração Tab */}
        <TabsContent value="migracao">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  Migração de Dados - Tenants para Settings
                </CardTitle>
                <CardDescription>
                  Sincroniza os dados de empresas cadastradas (tenants) com as configurações fiscais (settings).
                  Isso é necessário para que o sistema fiscal encontre os dados da empresa.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Passo 1: Verificar */}
                <div className="p-4 border rounded-lg bg-slate-50">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm">1</span>
                    Verificar Sincronização
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Primeiro, verifique quantos tenants precisam ser migrados. Esta ação é segura e não modifica nenhum dado.
                  </p>
                  <Button 
                    onClick={verificarSincronizacao} 
                    disabled={migracaoLoading}
                    variant="outline"
                  >
                    {migracaoLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    Verificar Status
                  </Button>
                  
                  {migracaoStatus && (
                    <div className="mt-4 p-4 bg-white rounded-lg border">
                      <p className="font-medium text-lg mb-2">{migracaoStatus.message}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div className="text-center p-3 bg-slate-100 rounded">
                          <p className="text-2xl font-bold">{migracaoStatus.resultado?.total_tenants || 0}</p>
                          <p className="text-sm text-slate-600">Total Tenants</p>
                        </div>
                        <div className="text-center p-3 bg-green-100 rounded">
                          <p className="text-2xl font-bold text-green-700">{migracaoStatus.resultado?.sincronizados || 0}</p>
                          <p className="text-sm text-slate-600">Sincronizados</p>
                        </div>
                        <div className="text-center p-3 bg-amber-100 rounded">
                          <p className="text-2xl font-bold text-amber-700">{migracaoStatus.resultado?.sem_settings_company?.length || 0}</p>
                          <p className="text-sm text-slate-600">Sem Settings</p>
                        </div>
                        <div className="text-center p-3 bg-blue-100 rounded">
                          <p className="text-2xl font-bold text-blue-700">{migracaoStatus.resultado?.company_sem_cnpj?.length || 0}</p>
                          <p className="text-sm text-slate-600">Sem CNPJ</p>
                        </div>
                      </div>
                      
                      {migracaoStatus.resultado?.sem_settings_company?.length > 0 && (
                        <div className="mt-4">
                          <p className="font-medium text-sm mb-2">Tenants sem Settings Company:</p>
                          <ul className="text-sm text-slate-600 list-disc list-inside">
                            {migracaoStatus.resultado.sem_settings_company.map((t, i) => (
                              <li key={i}>{t.company_name} ({t.tenant_id})</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Passo 2: Executar Migração */}
                <div className="p-4 border rounded-lg bg-slate-50">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">2</span>
                    Executar Migração
                  </h3>
                  <p className="text-sm text-slate-600 mb-2">
                    Executa a migração para todos os tenants que precisam. 
                  </p>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Seguro:</strong> Esta migração NÃO sobrescreve dados existentes. 
                      Apenas cria settings faltantes e preenche campos vazios com dados do tenant.
                    </p>
                  </div>
                  <Button 
                    onClick={executarMigracao} 
                    disabled={migracaoLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {migracaoLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <PlayCircle className="w-4 h-4 mr-2" />
                    )}
                    Executar Migração
                  </Button>
                  
                  {migracaoResultado && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="font-medium text-green-800 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5" />
                        {migracaoResultado.message}
                      </p>
                      {migracaoResultado.resultados && (
                        <div className="mt-2 text-sm text-green-700">
                          <p>Settings Company criados: {migracaoResultado.resultados.settings_company_criados}</p>
                          <p>Settings Company atualizados: {migracaoResultado.resultados.settings_company_atualizados}</p>
                          <p>Settings Fiscal criados: {migracaoResultado.resultados.settings_fiscal_criados}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Informações */}
                <div className="p-4 border rounded-lg bg-amber-50 border-amber-200">
                  <h3 className="font-semibold mb-2 flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="w-5 h-5" />
                    Quando usar esta migração?
                  </h3>
                  <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                    <li>Após atualizar o sistema para a nova versão</li>
                    <li>Quando clientes reclamam que o sistema fiscal não encontra dados da empresa</li>
                    <li>Para garantir que todos os tenants têm configurações fiscais iniciais</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Blog Tab */}
        <TabsContent value="blog">
          <BlogAdminPanel />
        </TabsContent>

        <TabsContent value="depoimentos">
          <TestimonialsAdminPanel />
        </TabsContent>
      </Tabs>

      {/* Tenant Details Modal */}
      <SimpleModal open={showTenantModal} onClose={() => setShowTenantModal(false)} title="Detalhes da Empresa">
        {tenantDetails && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Empresa</Label>
                <p className="font-semibold">{tenantDetails.company_name}</p>
              </div>
              <div>
                <Label className="text-slate-500">Email</Label>
                <p className="font-semibold">{tenantDetails.owner_email}</p>
              </div>
              <div>
                <Label className="text-slate-500">Plano</Label>
                <p className="font-semibold">{plans.find(p => p.id === tenantDetails.plan_id)?.name}</p>
              </div>
              <div>
                <Label className="text-slate-500">Status</Label>
                <p className="font-semibold capitalize">{tenantDetails.status}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-4 gap-4 pt-4 border-t">
              <div className="text-center p-3 bg-slate-50 rounded">
                <p className="text-2xl font-bold">{tenantDetails.stats.total_products}</p>
                <p className="text-xs text-slate-500">Produtos</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded">
                <p className="text-2xl font-bold">{tenantDetails.stats.total_customers}</p>
                <p className="text-xs text-slate-500">Clientes</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded">
                <p className="text-2xl font-bold">{tenantDetails.stats.total_sales}</p>
                <p className="text-xs text-slate-500">Vendas</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded">
                <p className="text-2xl font-bold">{tenantDetails.stats.total_users}</p>
                <p className="text-xs text-slate-500">Usuários</p>
              </div>
            </div>
            
            <div className="p-3 bg-emerald-50 rounded-lg">
              <p className="text-sm text-emerald-700">Vendas do Mês</p>
              <p className="text-xl font-bold text-emerald-700">
                {formatCurrency(tenantDetails.stats.month_sales_value)} 
                <span className="text-sm font-normal ml-2">({tenantDetails.stats.month_sales_count} vendas)</span>
              </p>
            </div>
            
            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={() => setShowTenantModal(false)}>Fechar</Button>
            </div>
          </div>
        )}
      </SimpleModal>

      {/* Edit Plan Modal */}
      {showPlanModal && editingPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowPlanModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Editar Plano</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPlanModal(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Nome do Plano *</Label>
                <Input
                  value={editingPlan.name}
                  onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                  placeholder="Ex: Básico, Profissional, Enterprise"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Input
                  value={editingPlan.description || ''}
                  onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })}
                  placeholder="Descrição curta do plano"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Preço Mensal (R$) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPlan.price}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Máx. Vendas/Mês</Label>
                  <Input
                    type="number"
                    value={editingPlan.max_sales_per_month}
                    onChange={(e) => setEditingPlan({ ...editingPlan, max_sales_per_month: parseInt(e.target.value) || -1 })}
                    placeholder="-1 = ilimitado"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Máx. Produtos</Label>
                  <Input
                    type="number"
                    value={editingPlan.max_products}
                    onChange={(e) => setEditingPlan({ ...editingPlan, max_products: parseInt(e.target.value) || -1 })}
                    placeholder="-1 = ilimitado"
                  />
                </div>
                <div>
                  <Label>Máx. Usuários</Label>
                  <Input
                    type="number"
                    value={editingPlan.max_users}
                    onChange={(e) => setEditingPlan({ ...editingPlan, max_users: parseInt(e.target.value) || -1 })}
                    placeholder="-1 = ilimitado"
                  />
                </div>
              </div>
              
              {/* Recursos/Features */}
              <div>
                <Label>Recursos do Plano</Label>
                <div className="mt-2 space-y-2">
                  {(editingPlan.features || []).map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => {
                          const newFeatures = [...(editingPlan.features || [])];
                          newFeatures[index] = e.target.value;
                          setEditingPlan({ ...editingPlan, features: newFeatures });
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newFeatures = (editingPlan.features || []).filter((_, i) => i !== index);
                          setEditingPlan({ ...editingPlan, features: newFeatures });
                        }}
                        className="text-rose-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newFeatures = [...(editingPlan.features || []), ''];
                      setEditingPlan({ ...editingPlan, features: newFeatures });
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Adicionar Recurso
                  </Button>
                </div>
              </div>

              {/* Módulos Habilitados */}
              <div>
                <Label className="text-base font-semibold">Módulos Habilitados</Label>
                <p className="text-sm text-slate-500 mb-3">Selecione quais módulos estão incluídos neste plano</p>
                <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <ShoppingCart className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">PDV</p>
                        <p className="text-xs text-slate-500">Ponto de Venda</p>
                      </div>
                    </div>
                    <Switch
                      checked={editingPlan.modules?.pdv ?? true}
                      onCheckedChange={(checked) => setEditingPlan({ 
                        ...editingPlan, 
                        modules: { ...(editingPlan.modules || {}), pdv: checked }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                        <MessageCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">WhatsApp Bot</p>
                        <p className="text-xs text-slate-500">Bot de vendas IA</p>
                      </div>
                    </div>
                    <Switch
                      checked={editingPlan.modules?.whatsapp ?? false}
                      onCheckedChange={(checked) => setEditingPlan({ 
                        ...editingPlan, 
                        modules: { ...(editingPlan.modules || {}), whatsapp: checked }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <ShoppingBag className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">Vendas Online</p>
                        <p className="text-xs text-slate-500">Catálogo e pedidos online</p>
                      </div>
                    </div>
                    <Switch
                      checked={editingPlan.modules?.online ?? false}
                      onCheckedChange={(checked) => setEditingPlan({ 
                        ...editingPlan, 
                        modules: { ...(editingPlan.modules || {}), online: checked }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Store className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">Marketplaces</p>
                        <p className="text-xs text-slate-500">Integração com marketplaces</p>
                      </div>
                    </div>
                    <Switch
                      checked={editingPlan.modules?.marketplace ?? false}
                      onCheckedChange={(checked) => setEditingPlan({ 
                        ...editingPlan, 
                        modules: { ...(editingPlan.modules || {}), marketplace: checked }
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowPlanModal(false)}>Cancelar</Button>
              <Button onClick={handleUpdatePlan} className="bg-emerald-600 hover:bg-emerald-700">
                Salvar Alterações
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subscription Modal */}
      {showSubscriptionModal && editingSubscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowSubscriptionModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold mb-4">Editar Assinatura: {editingSubscription?.company_name}</h2>
            <div className="space-y-4">
              <div>
                <Label>Alterar Plano</Label>
                <select
                  value={editingSubscription.newPlanId}
                  onChange={(e) => setEditingSubscription({ ...editingSubscription, newPlanId: e.target.value })}
                  className="w-full h-10 px-3 border border-slate-300 rounded-md"
                >
                  <option value="">Manter atual ({editingSubscription.plan_name})</option>
                  {plans.map(p => (
                    <option key={p.id} value={p.id}>{p.name} - {formatCurrency(p.price)}/mês</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Alterar Status</Label>
                <select
                  value={editingSubscription.newStatus}
                  onChange={(e) => setEditingSubscription({ ...editingSubscription, newStatus: e.target.value })}
                  className="w-full h-10 px-3 border border-slate-300 rounded-md"
                >
                  <option value="">Manter atual ({editingSubscription.status})</option>
                  <option value="active">Ativo</option>
                  <option value="trial">Trial</option>
                  <option value="suspended">Suspenso</option>
                  <option value="expired">Expirado</option>
                </select>
              </div>
              <div>
                <Label>Estender Assinatura (dias)</Label>
                <Input
                  type="number"
                  placeholder="Ex: 30"
                  value={editingSubscription.extendDays}
                  onChange={(e) => setEditingSubscription({ ...editingSubscription, extendDays: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowSubscriptionModal(false)}>Cancelar</Button>
              <Button onClick={handleUpdateSubscription}>Salvar Alterações</Button>
            </div>
          </div>
        </div>
      )}

      {/* New Plan Modal */}
      {showNewPlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowNewPlanModal(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-2">Criar Novo Plano</h2>
            <p className="text-slate-500 text-sm mb-4">Configure os detalhes do novo plano</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>ID do Plano</Label>
                  <Input
                    placeholder="ex: premium"
                    value={newPlan.id}
                    onChange={(e) => setNewPlan({ ...newPlan, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                  />
                </div>
                <div>
                  <Label>Nome do Plano *</Label>
                  <Input
                    placeholder="ex: Premium"
                    value={newPlan.name}
                    onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Preço Mensal (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newPlan.price}
                  onChange={(e) => setNewPlan({ ...newPlan, price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Máx. Produtos</Label>
                  <Input
                    type="number"
                    value={newPlan.max_products}
                    onChange={(e) => setNewPlan({ ...newPlan, max_products: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Máx. Usuários</Label>
                  <Input
                    type="number"
                    value={newPlan.max_users}
                    onChange={(e) => setNewPlan({ ...newPlan, max_users: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Vendas/Mês</Label>
                  <Input
                    type="number"
                    value={newPlan.max_sales_per_month}
                    onChange={(e) => setNewPlan({ ...newPlan, max_sales_per_month: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div>
                <Label>Funcionalidades</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    placeholder="Adicionar funcionalidade..."
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <Button type="button" onClick={addFeature} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {newPlan.features.map((f, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-sm flex items-center gap-2">
                      {f}
                      <button onClick={() => removeFeature(i)} className="text-slate-400 hover:text-rose-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Módulos Habilitados */}
              <div>
                <Label className="text-base font-semibold">Módulos Habilitados</Label>
                <p className="text-sm text-slate-500 mb-3">Selecione quais módulos estão incluídos neste plano</p>
                <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <ShoppingCart className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">PDV</p>
                        <p className="text-xs text-slate-500">Ponto de Venda</p>
                      </div>
                    </div>
                    <Switch
                      checked={newPlan.modules?.pdv ?? true}
                      onCheckedChange={(checked) => setNewPlan({ 
                        ...newPlan, 
                        modules: { ...(newPlan.modules || {}), pdv: checked }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                        <MessageCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium">WhatsApp Bot</p>
                        <p className="text-xs text-slate-500">Bot de vendas IA</p>
                      </div>
                    </div>
                    <Switch
                      checked={newPlan.modules?.whatsapp ?? false}
                      onCheckedChange={(checked) => setNewPlan({ 
                        ...newPlan, 
                        modules: { ...(newPlan.modules || {}), whatsapp: checked }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <ShoppingBag className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">Vendas Online</p>
                        <p className="text-xs text-slate-500">Catálogo e pedidos online</p>
                      </div>
                    </div>
                    <Switch
                      checked={newPlan.modules?.online ?? false}
                      onCheckedChange={(checked) => setNewPlan({ 
                        ...newPlan, 
                        modules: { ...(newPlan.modules || {}), online: checked }
                      })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Store className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium">Marketplaces</p>
                        <p className="text-xs text-slate-500">Integração com marketplaces</p>
                      </div>
                    </div>
                    <Switch
                      checked={newPlan.modules?.marketplace ?? false}
                      onCheckedChange={(checked) => setNewPlan({ 
                        ...newPlan, 
                        modules: { ...(newPlan.modules || {}), marketplace: checked }
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowNewPlanModal(false)}>Cancelar</Button>
              <Button onClick={handleCreatePlan} className="bg-emerald-600 hover:bg-emerald-700">
                Criar Plano
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
